import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, fmtErr } from "../api";
import { Button, Card, CardContent, Input, Label, Badge } from "../components/ui";
import { Plus, Trash2, Download } from "lucide-react";

export default function Agents() {
  const [agents, setAgents] = useState([]);
  const [creating, setCreating] = useState(false);
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [issued, setIssued] = useState(null);
  const wsUrl = `${window.location.origin.replace(/^http/, "ws")}/api/ws/audio-agent`;
  const downloadUrl = `${window.location.origin}/api/ui/kgv-audio-agent-windows.zip`;

  const load = async () => {
    try { const r = await api.get("/status"); setAgents(r.data.agents || []); }
    catch { }
  };
  useEffect(() => { load(); const id = setInterval(load, 3000); return () => clearInterval(id); }, []);

  const create = async () => {
    if (!name.trim()) return;
    try {
      const r = await api.post("/agents", { name, description });
      setIssued(r.data);
      setName(""); setDescription(""); setCreating(false);
      await load();
    } catch (e) { toast.error(fmtErr(e)); }
  };
  const remove = async (id) => {
    if (!window.confirm("Remover agente?")) return;
    try { await api.delete(`/agents/${id}`); await load(); toast.success("Agente removido"); }
    catch (e) { toast.error(fmtErr(e)); }
  };

  return (
    <div>
      <h1 className="font-display text-4xl uppercase leading-none mb-6">Agentes Windows</h1>

      <Card className="mb-4 border-[hsl(var(--primary))]/40">
        <CardContent className="p-6 flex items-center justify-between gap-4 flex-wrap">
          <div>
            <div className="font-display text-2xl uppercase tracking-tight">KGV Audio Agent</div>
            <div className="text-xs text-gray-400 mt-1">Windows portable v2 — extraia e execute <code className="text-[hsl(var(--primary))]">KGV Audio Agent.exe</code></div>
            <div className="text-xs text-gray-500 mt-1">v1.0.0 · 107 MB · Windows x64 · não requer instalação</div>
          </div>
          <a
            href={downloadUrl}
            className="inline-flex items-center gap-2 bg-[hsl(var(--primary))] text-black px-4 py-2 rounded-sm font-semibold uppercase tracking-widest text-xs hover:bg-[hsl(var(--primary))]/85"
            data-testid="download-agent-btn"
            download
          >
            <Download size={14} /> Baixar Agente
          </a>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-6 space-y-4">
          <div className="flex items-center justify-between">
            <div className="text-sm text-gray-400">Cada agente Windows precisa de um token único para conectar via WebSocket.</div>
            <Button data-testid="new-agent-btn" variant="outline" size="sm" onClick={() => setCreating(true)}><Plus size={14} className="mr-1" /> Novo agente</Button>
          </div>

          {creating && (
            <div className="p-4 border border-panel space-y-2 bg-black/30">
              <Label>Nome do agente</Label>
              <Input data-testid="agent-name" value={name} onChange={(e) => setName(e.target.value)} placeholder="PC-Recepção" />
              <Label>Descrição (opcional)</Label>
              <Input value={description} onChange={(e) => setDescription(e.target.value)} placeholder="Computador da recepção conectado à Bose S1" />
              <div className="flex gap-2 pt-2">
                <Button data-testid="agent-create" onClick={create}>Criar</Button>
                <Button variant="outline" onClick={() => { setCreating(false); setName(""); setDescription(""); }}>Cancelar</Button>
              </div>
            </div>
          )}

          {issued && (
            <div className="p-4 border border-[hsl(var(--primary))]/40 bg-[hsl(var(--primary))]/5 space-y-2">
              <div className="text-sm font-semibold uppercase tracking-widest">Credenciais geradas — copie agora!</div>
              <div className="text-xs text-gray-400">Este token só será mostrado uma vez. Cole-o na configuração do agente Windows.</div>
              <div className="p-2 bg-black/50 border border-panel">
                <div className="text-[10px] uppercase tracking-widest text-gray-500">WebSocket URL</div>
                <div className="font-mono-data text-xs select-all break-all">{wsUrl}?token={issued.token}</div>
              </div>
              <div className="p-2 bg-black/50 border border-panel">
                <div className="text-[10px] uppercase tracking-widest text-gray-500">Agent ID</div>
                <div className="font-mono-data text-xs select-all break-all">{issued.agent_id}</div>
              </div>
              <div className="p-2 bg-black/50 border border-panel">
                <div className="text-[10px] uppercase tracking-widest text-gray-500">Token</div>
                <div className="font-mono-data text-xs select-all break-all" data-testid="issued-token">{issued.token}</div>
              </div>
              <Button size="sm" variant="outline" onClick={() => setIssued(null)}>Fechar</Button>
            </div>
          )}

          <div className="space-y-1">
            {agents.length === 0 && <div className="text-sm text-gray-500 py-4">Nenhum agente cadastrado.</div>}
            {agents.map((a) => (
              <div key={a.id} className="flex items-center gap-3 p-3 border border-panel" data-testid={`agent-row-${a.name}`}>
                <Badge className={a.online ? "bg-green-500/20 text-green-300" : "bg-white/5 text-gray-500"}>
                  {a.online ? "ONLINE" : "OFFLINE"}
                </Badge>
                <div className="flex-1">
                  <div className="font-semibold">{a.name}</div>
                  <div className="text-xs text-gray-500">
                    {a.hostname || "—"} · {a.output_device || "dispositivo padrão"} · v{a.version || "?"}
                  </div>
                </div>
                <div className="text-[10px] uppercase tracking-widest text-gray-500">{a.state}</div>
                <button className="text-red-400 hover:bg-red-400/10 p-2 rounded-sm" onClick={() => remove(a.agent_id)}><Trash2 size={14} /></button>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
